// src/components/navigation/NavigationProvider.tsx
// src/components/navigation/NavigationProvider.tsx - FIXED VERSION
import React, { createContext, useContext, useState, ReactNode, useMemo, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export type NavItem = {
  id: string;
  name: string;
  path: string;
  icon?: string;
  category?: string;
  isPremium?: boolean;
  requiredTier?: string;
};

interface NavigationContextType {
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
  navItems: NavItem[];
  getUserNavItems: () => NavItem[];
  tierLoading: boolean;
  dbLoading: boolean;
  groupedNavItems: Record<string, NavItem[]>;
  hasAccess: (path: string) => boolean;
  toggleSidebar: () => void;
  refreshRoutes: () => Promise<void>;
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = (): NavigationContextType => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }
  return context;
};

// ✅ MOVED: Helper function to top level to avoid hoisting issues
const getRequiredTier = (tierReq: any) => {
  if (!tierReq) return 'basic';
  if (tierReq.admin) return 'admin';
  if (tierReq.agency) return 'agency';
  if (tierReq.premium_pro) return 'premium_pro';
  if (tierReq.premium) return 'premium';
  return 'basic';
};

// Props interface
interface NavigationProviderProps {
  children: ReactNode;
  tierLoading?: boolean;
  checkAccess?: (path: string) => boolean;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = ({ 
  children, 
  tierLoading = false,
  checkAccess = () => true
}) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [dbRoutes, setDbRoutes] = useState<any[]>([]);
  const [dbLoading, setDbLoading] = useState(true);

  // ✅ ADDED: Fetch routes from Supabase
  const fetchRoutes = async () => {
    try {
      console.log('🔄 NavigationProvider: Fetching routes from database...');
      
      const { data, error } = await supabase
        .from('tier_route_access')
        .select('*')
        .eq('is_active', true)
        .order('display_order', { ascending: true })
        .order('route_path', { ascending: true });

      if (error) {
        console.error('❌ Error fetching routes:', error);
        setDbRoutes([]);
      } else {
        console.log('✅ NavigationProvider: Fetched', data?.length, 'routes from database');
        setDbRoutes(data || []);
      }
    } catch (error) {
      console.error('❌ NavigationProvider: Exception fetching routes:', error);
      setDbRoutes([]);
    } finally {
      setDbLoading(false);
    }
  };

  // ✅ ADDED: Refresh function
  const refreshRoutes = async () => {
    setDbLoading(true);
    await fetchRoutes();
  };

  // Fetch routes on mount
  useEffect(() => {
    fetchRoutes();
  }, []);

  // ✅ FIXED: navItems uses dbRoutes from database (no hoisting issue)
  const navItems = useMemo(() => {
    console.log('🔄 NavigationProvider: Processing navItems from', dbRoutes.length, 'database routes');
    
    if (dbLoading || tierLoading) {
      console.log('⏳ NavigationProvider: Still loading, returning empty nav items');
      return [];
    }
    
    // Filter accessible routes based on tier permissions
    const accessibleRoutes = dbRoutes.filter(route => {
      const hasAccess = checkAccess(route.route_path);
      if (!hasAccess) {
        console.log(`🚫 NavigationProvider: Access denied for ${route.route_path}`);
      }
      return hasAccess;
    });
    
    console.log(`✅ NavigationProvider: ${accessibleRoutes.length} accessible routes of ${dbRoutes.length} total`);
    
    // Convert to NavItem format
    return accessibleRoutes.map(route => ({
      id: route.id || route.route_path,
      name: route.route_name || route.route_path.replace('/', '').replace(/-/g, ' '),
      path: route.route_path,
      icon: route.icon || '✨',
      category: route.hub_category || 'core',
      isPremium: route.tier_requirements?.premium_pro || false,
      requiredTier: getRequiredTier(route.tier_requirements) // ✅ NOW WORKS - function is defined
    }));
  }, [dbRoutes, dbLoading, tierLoading, checkAccess]);

  // ✅ REQUIRED: Function that SmartSidebar expects
  const getUserNavItems = (): NavItem[] => {
    console.log('📊 NavigationProvider: getUserNavItems called, returning', navItems.length, 'items');
    return navItems;
  };

  // Group nav items by category
  const groupedNavItems = useMemo(() => {
    const groups: Record<string, NavItem[]> = {};
    
    navItems.forEach(item => {
      const category = item.category || 'core';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(item);
    });
    
    console.log('📂 NavigationProvider: Grouped into', Object.keys(groups).length, 'categories');
    return groups;
  }, [navItems]);

  const toggleSidebar = () => {
    setIsSidebarOpen(prev => !prev);
  };

  const hasAccess = (path: string): boolean => {
    return checkAccess ? checkAccess(path) : true;
  };

  // ✅ UPDATED: Context value with new properties
  const value: NavigationContextType = {
    isSidebarOpen,
    setIsSidebarOpen,
    navItems,
    getUserNavItems,
    tierLoading,
    dbLoading,
    groupedNavItems,
    hasAccess,
    toggleSidebar,
    refreshRoutes
  };

  console.log('✅ NavigationProvider: Context updated with', navItems.length, 'nav items');

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};

